import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { setTransactions } from '../store/salesSlice';
import GravityLogo from './GravityLogo';
import { setProductData } from '../store/productSlice';
import { setAdminData } from '../store/adminSlice';
import { PW_Transactions_List_API, PW_Products_API, PW_Stores_API, PW_Users_API } from '../Server/ApiV2';
import Spinner from './Spinner';

export default function Transactions() {
  const dispatch = useDispatch();
  const { transactions } = useSelector(s => s.sales);
  const { products } = useSelector(s => s.product);
  const { stores, users } = useSelector(s => s.admin);
  const [pageLoading, setPageLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [txnRes, prodRes, storeRes, userRes] = await Promise.all([
        PW_Transactions_List_API(), PW_Products_API(), PW_Stores_API(), PW_Users_API(),
      ]);
      if (cancelled) return;
      if (txnRes?.data?.success) dispatch(setTransactions(txnRes.data.data));
      if (prodRes?.data?.success) dispatch(setProductData(prodRes.data.data));
      if (storeRes?.data?.success) dispatch(setAdminData({ stores: storeRes.data.data }));
      if (userRes?.data?.success) {
        const u = userRes.data.data;
        dispatch(setAdminData({ users: u.users, userStoreRights: u.userStoreRights, userTypes: u.userTypes }));
      }
      setPageLoading(false);
    })();
    return () => { cancelled = true; };
  }, [dispatch]);
  const [viewTxn, setViewTxn] = useState(null);

  const [selStores, setSelStores] = useState([]);
  const [selUsers, setSelUsers] = useState([]);
  const [selProducts, setSelProducts] = useState([]);
  const [selResult, setSelResult] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [timeFrom, setTimeFrom] = useState('');
  const [timeTo, setTimeTo] = useState('');
  const [storeOpen, setStoreOpen] = useState(false);
  const [userOpen, setUserOpen] = useState(false);
  const [productOpen, setProductOpen] = useState(false);

  const [applied, setApplied] = useState({ stores: [], users: [], products: [], result: '', dateFrom: '', dateTo: '', timeFrom: '', timeTo: '' });
  const [hasApplied, setHasApplied] = useState(false);

  const allStores = useMemo(() => stores.map(s => s.name).sort(), [stores]);
  const allUsers = useMemo(() => users.map(u => u.name).sort(), [users]);
  const allProducts = useMemo(() => products.map(p => p.name).sort(), [products]);

  const filtered = useMemo(() => {
    let list = transactions;
    if (applied.stores.length) list = list.filter(t => applied.stores.includes(t.storeName));
    if (applied.users.length) list = list.filter(t => applied.users.includes(t.userName));
    if (applied.products.length) list = list.filter(t => applied.products.includes(t.productName));
    if (applied.result === 'win') list = list.filter(t => t.isWinner);
    if (applied.result === 'no') list = list.filter(t => !t.isWinner);
    if (applied.dateFrom) list = list.filter(t => t.timestamp >= applied.dateFrom);
    if (applied.dateTo) list = list.filter(t => t.timestamp <= applied.dateTo + 'T23:59:59');
    if (applied.timeFrom) list = list.filter(t => (t.timestamp.split('T')[1] || '').substring(0, 5) >= applied.timeFrom);
    if (applied.timeTo) list = list.filter(t => (t.timestamp.split('T')[1] || '').substring(0, 5) <= applied.timeTo);
    return list;
  }, [transactions, applied]);

  const toggleChip = (val, sel, setSel) => setSel(prev => prev.includes(val) ? prev.filter(v => v !== val) : [...prev, val]);
  const toggleAll = (all, sel, setSel) => setSel(sel.length === all.length ? [] : [...all]);
  const [filterError, setFilterError] = useState('');
  const handleApply = () => {
    if (!selStores.length && !selUsers.length && !selProducts.length && !selResult && !dateFrom && !dateTo && !timeFrom && !timeTo) {
      setFilterError('Select at least one filter');
      return;
    }
    if ((dateFrom && !dateTo) || (!dateFrom && dateTo)) {
      setFilterError('Both From Date and To Date are required');
      return;
    }
    if (dateFrom && dateTo && dateFrom > dateTo) {
      setFilterError('From Date cannot be greater than To Date');
      return;
    }
    if ((timeFrom && !timeTo) || (!timeFrom && timeTo)) {
      setFilterError('Both From Time and To Time are required');
      return;
    }
    if (timeFrom && timeTo && timeFrom > timeTo) {
      setFilterError('From Time cannot be greater than To Time');
      return;
    }
    setFilterError('');
    setHasApplied(true);
    setApplied({ stores: selStores, users: selUsers, products: selProducts, result: selResult, dateFrom, dateTo, timeFrom, timeTo });
  };
  const clearFilters = () => { setSelStores([]); setSelUsers([]); setSelProducts([]); setSelResult(''); setDateFrom(''); setDateTo(''); setTimeFrom(''); setTimeTo(''); setFilterError(''); setHasApplied(false); setApplied({ stores: [], users: [], products: [], result: '', dateFrom: '', dateTo: '', timeFrom: '', timeTo: '' }); };
  const hasFilters = applied.stores.length > 0 || applied.users.length > 0 || applied.products.length > 0 || applied.result || applied.dateFrom || applied.dateTo || applied.timeFrom || applied.timeTo;

  const storeRef = useRef(null);
  const userRef = useRef(null);
  const productRef = useRef(null);
  useEffect(() => {
    const handler = (e) => {
      if (storeRef.current && !storeRef.current.contains(e.target)) setStoreOpen(false);
      if (userRef.current && !userRef.current.contains(e.target)) setUserOpen(false);
      if (productRef.current && !productRef.current.contains(e.target)) setProductOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  if (pageLoading) return <div className="page-loading"><Spinner /></div>;

  return (
    <div>
      <div className="page-header">
        <div>
          <h2>Transactions</h2>
          <div className="page-header-sub">Coupon sales transaction log</div>
        </div>
      </div>

      <div className="card">
        <div className="card-title">Transaction Log {hasApplied ? `(${filtered.length}${hasFilters ? ` of ${transactions.length}` : ''})` : `(${transactions.length})`}</div>
        <div className="card-body">
          <div className="dash-filters">
            <div className="dash-filter-group" ref={storeRef}>
              <div className="text-sm text-muted mb-4">Store</div>
              <div className="dash-csv-select" onClick={() => setStoreOpen(!storeOpen)}>
                <span>{selStores.length ? selStores.join(', ') : 'All Stores'}</span>
                <span className="dash-csv-arrow">{storeOpen ? '▲' : '▼'}</span>
              </div>
              {storeOpen && (
                <div className="dash-csv-dropdown">
                  <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #e0dce8', padding: '6px 8px' }}>
                    <label className="dash-csv-option" style={{ fontWeight: 600, padding: 0 }}>
                      <input type="checkbox" checked={selStores.length === allStores.length && allStores.length > 0} onChange={() => toggleAll(allStores, selStores, setSelStores)} />
                      <span>Select All</span>
                    </label>
                    {selStores.length > 0 && <span style={{ color: '#e74c3c', cursor: 'pointer', fontSize: 12, alignSelf: 'center' }} onClick={() => setSelStores([])}>Clear All</span>}
                  </div>
                  {allStores.map(s => (
                    <label key={s} className="dash-csv-option">
                      <input type="checkbox" checked={selStores.includes(s)} onChange={() => toggleChip(s, selStores, setSelStores)} />
                      <span>{s}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
            <div className="dash-filter-group" ref={userRef}>
              <div className="text-sm text-muted mb-4">User</div>
              <div className="dash-csv-select" onClick={() => setUserOpen(!userOpen)}>
                <span>{selUsers.length ? selUsers.join(', ') : 'All Users'}</span>
                <span className="dash-csv-arrow">{userOpen ? '▲' : '▼'}</span>
              </div>
              {userOpen && (
                <div className="dash-csv-dropdown">
                  <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #e0dce8', padding: '6px 8px' }}>
                    <label className="dash-csv-option" style={{ fontWeight: 600, padding: 0 }}>
                      <input type="checkbox" checked={selUsers.length === allUsers.length && allUsers.length > 0} onChange={() => toggleAll(allUsers, selUsers, setSelUsers)} />
                      <span>Select All</span>
                    </label>
                    {selUsers.length > 0 && <span style={{ color: '#e74c3c', cursor: 'pointer', fontSize: 12, alignSelf: 'center' }} onClick={() => setSelUsers([])}>Clear All</span>}
                  </div>
                  {allUsers.map(u => (
                    <label key={u} className="dash-csv-option">
                      <input type="checkbox" checked={selUsers.includes(u)} onChange={() => toggleChip(u, selUsers, setSelUsers)} />
                      <span>{u}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
            <div className="dash-filter-group" ref={productRef}>
              <div className="text-sm text-muted mb-4">Product</div>
              <div className="dash-csv-select" onClick={() => setProductOpen(!productOpen)}>
                <span>{selProducts.length ? selProducts.join(', ') : 'All Products'}</span>
                <span className="dash-csv-arrow">{productOpen ? '▲' : '▼'}</span>
              </div>
              {productOpen && (
                <div className="dash-csv-dropdown">
                  <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #e0dce8', padding: '6px 8px' }}>
                    <label className="dash-csv-option" style={{ fontWeight: 600, padding: 0 }}>
                      <input type="checkbox" checked={selProducts.length === allProducts.length && allProducts.length > 0} onChange={() => toggleAll(allProducts, selProducts, setSelProducts)} />
                      <span>Select All</span>
                    </label>
                    {selProducts.length > 0 && <span style={{ color: '#e74c3c', cursor: 'pointer', fontSize: 12, alignSelf: 'center' }} onClick={() => setSelProducts([])}>Clear All</span>}
                  </div>
                  {allProducts.map(p => (
                    <label key={p} className="dash-csv-option">
                      <input type="checkbox" checked={selProducts.includes(p)} onChange={() => toggleChip(p, selProducts, setSelProducts)} />
                      <span>{p}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
            <div className="dash-filter-group">
              <div className="text-sm text-muted mb-4">Result</div>
              <select className="form-input-sm" value={selResult} onChange={e => setSelResult(e.target.value)} style={{ minWidth: 100 }}>
                <option value="">All</option>
                <option value="win">Win</option>
                <option value="no">No Prize</option>
              </select>
            </div>
            <div className="dash-filter-group">
              <div className="text-sm text-muted mb-4">Date Range</div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input type="date" className="form-input-sm" value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
                <span className="text-sm text-muted">to</span>
                <input type="date" className="form-input-sm" value={dateTo} onChange={e => setDateTo(e.target.value)} />
              </div>
            </div>
            <div className="dash-filter-group">
              <div className="text-sm text-muted mb-4">Time Range</div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input type="time" className="form-input-sm" value={timeFrom} onChange={e => setTimeFrom(e.target.value)} />
                <span className="text-sm text-muted">to</span>
                <input type="time" className="form-input-sm" value={timeTo} onChange={e => setTimeTo(e.target.value)} />
              </div>
            </div>
            <div className="dash-filter-actions">
              <button className="btn btn-sm btn-success" onClick={handleApply}>Apply</button>
              {hasFilters && <button className="btn btn-sm btn-outline" onClick={clearFilters}>Clear</button>}
            </div>
            {filterError && <div style={{ color: '#e74c3c', fontSize: 13, marginTop: 8, width: '100%' }}>{filterError}</div>}
          </div>
          {!hasApplied && (
            <div className="empty-state">
              <GravityLogo size={48} circle />
              <p>Select filters and click <b>Apply</b> to view transactions.</p>
            </div>
          )}
          {hasApplied && filtered.length > 0 && (() => {
            const wins = filtered.filter(t => t.isWinner);
            const totalPrize = wins.reduce((s, t) => s + (t.prize || 0), 0);
            const totalBonus = wins.reduce((s, t) => s + (t.bonusAmount || 0), 0);
            const revenue = filtered.reduce((s, t) => s + (products.find(p => p.id === t.productId)?.rsp || 0), 0);
            const pnl = revenue - totalPrize;
            const avgPrize = wins.length ? Math.round(totalPrize / wins.length) : 0;
            return (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 10, margin: '12px 0 16px' }}>
                <div style={{ background: '#f5f3fa', borderRadius: 8, padding: '10px 14px', textAlign: 'center' }}>
                  <div style={{ fontSize: 20, fontWeight: 700, color: '#2d1b69' }}>{filtered.length}</div>
                  <div style={{ fontSize: 11, color: '#6b6b80', fontWeight: 600 }}>TOTAL</div>
                </div>
                <div style={{ background: '#e8f5e9', borderRadius: 8, padding: '10px 14px', textAlign: 'center' }}>
                  <div style={{ fontSize: 20, fontWeight: 700, color: '#00895e' }}>{wins.length}</div>
                  <div style={{ fontSize: 11, color: '#6b6b80', fontWeight: 600 }}>WINNERS</div>
                </div>
                <div style={{ background: '#f5f3fa', borderRadius: 8, padding: '10px 14px', textAlign: 'center' }}>
                  <div style={{ fontSize: 20, fontWeight: 700, color: '#2d1b69' }}>{filtered.length ? (wins.length / filtered.length * 100).toFixed(1) : 0}%</div>
                  <div style={{ fontSize: 11, color: '#6b6b80', fontWeight: 600 }}>WIN RATE</div>
                </div>
                <div style={{ background: '#f0f7ff', borderRadius: 8, padding: '10px 14px', textAlign: 'center' }}>
                  <div style={{ fontSize: 20, fontWeight: 700, color: '#1565c0' }}>{revenue.toLocaleString()}</div>
                  <div style={{ fontSize: 11, color: '#6b6b80', fontWeight: 600 }}>REVENUE</div>
                </div>
                <div style={{ background: '#fff8e6', borderRadius: 8, padding: '10px 14px', textAlign: 'center' }}>
                  <div style={{ fontSize: 20, fontWeight: 700, color: '#c9a227' }}>{totalPrize.toLocaleString()}</div>
                  <div style={{ fontSize: 11, color: '#6b6b80', fontWeight: 600 }}>PRIZES PAID</div>
                </div>
                <div style={{ background: '#f5f3fa', borderRadius: 8, padding: '10px 14px', textAlign: 'center' }}>
                  <div style={{ fontSize: 20, fontWeight: 700, color: '#c9a227' }}>{avgPrize.toLocaleString()}</div>
                  <div style={{ fontSize: 11, color: '#6b6b80', fontWeight: 600 }}>AVG PRIZE</div>
                </div>
                {totalBonus > 0 && (
                  <div style={{ background: '#fdf6e3', borderRadius: 8, padding: '10px 14px', textAlign: 'center' }}>
                    <div style={{ fontSize: 20, fontWeight: 700, color: '#c9a227' }}>{totalBonus.toLocaleString()}</div>
                    <div style={{ fontSize: 11, color: '#6b6b80', fontWeight: 600 }}>BONUS PAID</div>
                  </div>
                )}
                <div style={{ background: pnl >= 0 ? '#e8f5e9' : '#fde8e8', borderRadius: 8, padding: '10px 14px', textAlign: 'center' }}>
                  <div style={{ fontSize: 20, fontWeight: 700, color: pnl >= 0 ? '#00895e' : '#e74c3c' }}>{pnl.toLocaleString()}</div>
                  <div style={{ fontSize: 11, color: '#6b6b80', fontWeight: 600 }}>P&L</div>
                </div>
              </div>
            );
          })()}
          {hasApplied && filtered.length > 0 && (() => {
            const byProduct = {};
            filtered.forEach(t => {
              const key = t.productId || 'unknown';
              if (!byProduct[key]) {
                const prod = products.find(p => p.id === key);
                byProduct[key] = { name: t.productName || key, rsp: prod?.rsp || 0, total: 0, wins: 0, prizeSum: 0 };
              }
              byProduct[key].total++;
              if (t.isWinner) { byProduct[key].wins++; byProduct[key].prizeSum += (t.prize || 0); }
            });
            const prodList = Object.values(byProduct).sort((a, b) => a.name.localeCompare(b.name));
            return (
              <div style={{ margin: '0 0 16px' }}>
                <div className="text-sm fw-600 mb-8" style={{ color: '#2d1b69' }}>Product-wise P&L</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 10 }}>
                  {prodList.map(p => {
                    const rev = p.total * p.rsp;
                    const pl = rev - p.prizeSum;
                    return (
                      <div key={p.name} style={{ background: '#fff', border: '1px solid #e0dce8', borderRadius: 10, padding: '12px 16px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                          <span className="fw-700" style={{ color: '#2d1b69', fontSize: 14 }}>{p.name}</span>
                          <span className="chip chip-purple" style={{ fontSize: 10 }}>{p.total} sold</span>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 6, textAlign: 'center' }}>
                          <div>
                            <div style={{ fontSize: 16, fontWeight: 700, color: '#1565c0' }}>{rev.toLocaleString()}</div>
                            <div style={{ fontSize: 10, color: '#6b6b80', fontWeight: 600 }}>REVENUE</div>
                          </div>
                          <div>
                            <div style={{ fontSize: 16, fontWeight: 700, color: '#c9a227' }}>{p.prizeSum.toLocaleString()}</div>
                            <div style={{ fontSize: 10, color: '#6b6b80', fontWeight: 600 }}>PRIZES</div>
                          </div>
                          <div>
                            <div style={{ fontSize: 16, fontWeight: 700, color: pl >= 0 ? '#00895e' : '#e74c3c' }}>{pl.toLocaleString()}</div>
                            <div style={{ fontSize: 10, color: '#6b6b80', fontWeight: 600 }}>P&L</div>
                          </div>
                        </div>
                        <div style={{ marginTop: 8, display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#6b6b80' }}>
                          <span>{p.wins}W / {p.total - p.wins}L</span>
                          <span>Win {(p.total ? (p.wins / p.total * 100).toFixed(1) : 0)}%</span>
                          <span>RSP {p.rsp}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })()}
          {hasApplied && filtered.length === 0 && (
            <div className="text-sm text-muted" style={{ padding: 16 }}>No transactions match the selected filters.</div>
          )}
          {hasApplied && filtered.length > 0 && (
            <div style={{ maxHeight: 600, overflow: 'auto' }}>
              <table className="data-table" style={{ fontSize: 12 }}>
                <thead className="sticky-header">
                  <tr>
                    <th style={{ padding: '8px 8px' }}>Trans ID</th>
                    <th style={{ padding: '8px 6px' }}>Sel#</th>
                    <th style={{ padding: '8px 6px' }}>Date</th>
                    <th style={{ padding: '8px 6px' }}>Time</th>
                    <th style={{ padding: '8px 6px' }}>User</th>
                    <th style={{ padding: '8px 6px' }}>Store</th>
                    <th style={{ padding: '8px 6px' }}>Product</th>
                    <th style={{ padding: '8px 6px' }}>Type</th>
                    <th style={{ padding: '8px 6px' }}>Result</th>
                    <th style={{ padding: '8px 6px', textAlign: 'right' }}>Prize</th>
                    <th style={{ padding: '8px 6px', textAlign: 'right' }}>Bonus</th>
                    <th style={{ padding: '8px 6px', textAlign: 'center' }}>Card</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(t => (
                    <tr key={t.id} className={t.isWinner ? 'winner-row' : ''}>
                      <td style={{ fontFamily: 'monospace', fontSize: 10, padding: '6px 8px', whiteSpace: 'nowrap' }}>{t.id}</td>
                      <td style={{ fontSize: 11, padding: '6px', textAlign: 'center' }}>{t.selectionNo || '—'}</td>
                      <td style={{ fontSize: 11, padding: '6px', whiteSpace: 'nowrap' }}>{new Date(t.timestamp).toLocaleDateString()}</td>
                      <td style={{ fontSize: 11, padding: '6px', whiteSpace: 'nowrap' }}>{new Date(t.timestamp).toLocaleTimeString()}</td>
                      <td style={{ fontSize: 11, padding: '6px' }}>{t.userName || '—'}</td>
                      <td style={{ fontSize: 11, padding: '6px' }}>{t.storeName}</td>
                      <td style={{ fontSize: 11, padding: '6px' }}>{t.productName}</td>
                      <td style={{ padding: '6px' }}><span className="chip chip-purple" style={{ fontSize: 10 }}>{t.typeId}</span></td>
                      <td style={{ padding: '6px' }}>
                        <span className={`chip ${t.isWinner ? 'chip-green' : ''}`} style={{ fontSize: 10 }}>
                          {t.isWinner ? 'Win' : 'No'}
                        </span>
                      </td>
                      <td style={{ textAlign: 'right', fontWeight: 600, color: t.isWinner ? '#c9a227' : '#999', padding: '6px' }}>
                        {t.isWinner ? t.prize.toLocaleString() : '—'}
                      </td>
                      <td style={{ textAlign: 'right', fontWeight: 600, color: t.bonusAmount > 0 ? '#c9a227' : '#999', padding: '6px' }}>
                        {t.bonusAmount > 0 ? t.bonusAmount.toLocaleString() : '—'}
                      </td>
                      <td style={{ textAlign: 'center', padding: '6px' }}>
                        {t.cardGrid?.length > 0 ? (
                          <span className="view-card-btn" onClick={() => setViewTxn(t)} title="View instance reward">⊞</span>
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {viewTxn && (
        <div className="modal-overlay" onClick={() => setViewTxn(null)}>
          <div className={`scratchcard-modal${viewTxn.isWinner ? ' scratchcard-modal-win' : ''}`} onClick={e => e.stopPropagation()}>
            <button className="scratchcard-close" onClick={() => setViewTxn(null)}>×</button>
            <div className="scratchcard-header">
              <div className="scratchcard-logo">★</div>
              <div className="scratchcard-title">{viewTxn.productName}</div>
              <div className="scratchcard-meta">
                <span className="chip chip-purple" style={{ fontSize: 10 }}>{viewTxn.typeId}</span>
                <span>{viewTxn.storeName}</span>
                <span>{new Date(viewTxn.timestamp).toLocaleString()}</span>
              </div>
            </div>
            <div className="scratchcard-grid">
              {viewTxn.cardGrid.flat().map((val, i) => (
                <div key={i} className={`scratchcard-cell${viewTxn.isWinner && (viewTxn.winningNumbers ? viewTxn.winningNumbers.includes(val) : val === viewTxn.winDenomination) ? ' scratchcard-cell-win' : ''}`}
                  style={{ animationDelay: `${i * 0.04}s` }}>
                  <span className="scratchcard-cell-val">{val.toLocaleString()}</span>
                </div>
              ))}
            </div>
            <div className="scratchcard-result">
              <div className="scratchcard-result-inner">
                {viewTxn.isWinner ? (
                  <>
                    <div className="scratchcard-result-badge win">WINNER</div>
                    <div className="scratchcard-prize">{viewTxn.prize.toLocaleString()}</div>
                    <div className="scratchcard-prize-label">Prize Amount</div>
                    {viewTxn.bonusAmount > 0 && (
                      <div style={{ marginTop: 4, fontSize: 12, color: '#c9a227', fontWeight: 600 }}>
                        (includes Bonus +{viewTxn.bonusAmount.toLocaleString()})
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <div className="scratchcard-result-badge lose">NO PRIZE</div>
                    <div className="scratchcard-nope">Better luck next time</div>
                  </>
                )}
              </div>
            </div>
            <div className="scratchcard-id">{viewTxn.id}</div>
          </div>
        </div>
      )}
    </div>
  );
}
